import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity, Award, Download, FileSignature, GraduationCap, Mail, Menu,
  RefreshCw, School, Sparkles, TrendingUp, Upload, Users,
} from "lucide-react";
import { API } from "../../lib/api";
import { fmtN, fmtPct } from "../../lib/format";
import { ROLE_LABELS } from "../../lib/dashConstants";
import { FilterSelect, MultiSelect } from "./Filters";
import { KpiCard, ChartCard } from "./Cards";
import { DonutChart, HorizontalBar, StatusLegend, TimelineChart } from "./Charts";
import Leaderboard from "./Leaderboard";
import UploadModal from "./UploadModal";
import DrillDownModal from "./DrillDownModal";
import InsightsPanel from "./InsightsPanel";
import DetailsTable from "./DetailsTable";
import Sidebar from "./Sidebar";
import UserManagement from "./UserManagement";
import ActivityFormView from "./ActivityFormView";
import LeadsListView from "./LeadsListView";
import LeadFormView from "./LeadFormView";
import LeadDetailView from "./LeadDetailView";
import {
  SalesComparisonView, ForecastView, NotificationPanel, AiAssistantPanel,
} from "./ExtraViews";
import { AdminMetricsPanel, ChatView } from "./SuperViews";
import EFTView, { TopPerformersPanel } from "./EFTView";
import { SalesTLDashboardPanel, SalesDashboardPanel, RMDashboardPanel } from "./RoleDashboardPanels";
import { AdminChartsPanel, SalesTLChartsPanel, SalesChartsPanel, RMChartsPanel } from "./RoleChartsPanels";
import GlobalSearch from "./GlobalSearch";
import ConversionFunnel from "./ConversionFunnel";
import { ActiveAccountView, ChurnAccountView, ApprovalView } from "./AccountViews";
import ProfileView from "./ProfileView";
import { DrilldownProvider } from "./DrilldownContext";

const INITIALS = (s) => (s || "U").trim().slice(0, 2).toUpperCase();

export default function Dashboard({ authUser, onLogout }) {
  const perms = authUser?.permissions || {};
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeView, setActiveView] = useState("dashboard");
  const [filterOpts, setFilterOpts] = useState({
    nama_sales: [], tahun_visit: [], tahun_ajaran: [], status_sekolah: [],
    dilakukan_via: [], tujuan_sales: [], tim_sales: [], kegiatan: [], total_rows: 0,
  });
  const [filters, setFilters] = useState({
    sales: "All", ta: "All", status: "All", via: "All",
    tujuan: "All", tim: "All", kegiatan: [], date_from: "", date_to: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);  // row data for edit, or null
  const [drill, setDrill] = useState(null);
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [leadFormMode, setLeadFormMode] = useState(null); // null | "create" | "edit"
  const [auditSending, setAuditSending] = useState(false);
  const [auditStatus, setAuditStatus] = useState(null); // {ok, msg}

  const loadFilterOpts = useCallback(() => {
    axios.get(`${API}/sales/filters`).then((r) => setFilterOpts(r.data)).catch(console.error);
  }, []);
  useEffect(() => { loadFilterOpts(); }, [loadFilterOpts]);

  const apiFilters = useMemo(() => {
    const p = {};
    if (filters.sales !== "All") p.sales = filters.sales;
    if (filters.ta !== "All") p.ta = filters.ta;
    if (filters.status !== "All") p.status = filters.status;
    if (filters.via !== "All") p.via = filters.via;
    if (filters.tujuan !== "All") p.tujuan = filters.tujuan;
    if (filters.tim !== "All") p.tim = filters.tim;
    if (filters.kegiatan.length > 0) p.kegiatan = filters.kegiatan.join(",");
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  }, [filters]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales/dashboard`, { params: apiFilters });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFilters]);
  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  const onReset = () => setFilters({ sales: "All", ta: "All", status: "All", via: "All", tujuan: "All", tim: "All", kegiatan: [], date_from: "", date_to: "" });
  const onDownload = () => {
    const qs = new URLSearchParams(apiFilters).toString();
    window.open(`${API}/sales/download/excel?${qs}`, "_blank");
  };
  const openDrill = (d) => setDrill(d);

  const handleEditRow = (row) => {
    setEditTarget(row);
    setActiveView("activities");
  };

  const sendAuditEmailNow = async () => {
    setAuditSending(true);
    setAuditStatus({ ok: true, msg: "Mengirim email audit log..." });
    try {
      const r = await axios.post(`${API}/admin/audit-email/test`);
      setAuditStatus({ ok: true, msg: `Email terkirim ke ${r.data.to} · ${r.data.total_events} event` });
    } catch (e) {
      setAuditStatus({ ok: false, msg: e?.response?.data?.detail || "Gagal mengirim email" });
    } finally {
      setAuditSending(false);
      setTimeout(() => setAuditStatus(null), 15000);
    }
  };

  return (
    <DrilldownProvider>
    <div className="app-shell">
      <Sidebar
        open={sidebarOpen} onClose={() => setSidebarOpen(false)}
        user={authUser} perms={perms} activeView={activeView}
        onNavigate={(view) => {
          if (view === "audit-email") { sendAuditEmailNow(); return; }
          setActiveView(view);
        }}
        onLogout={onLogout}
      />
      <NotificationPanel onSelectLead={(id) => {
        setSelectedLeadId(id);
        setLeadFormMode(null);
        setActiveView("leads");
      }} />
      <AiAssistantPanel />
      <button className="profile-trigger" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar">
        <span className="profile-avatar">{INITIALS(authUser?.username)}</span>
        <span>
          <span className="profile-name">{authUser?.username}</span><br/>
          <span className="profile-role">{ROLE_LABELS[authUser?.role] || authUser?.role}</span>
        </span>
        <Menu size={14} color="var(--muted)" style={{marginLeft: 4}} />
      </button>

      <header className="topbar">
        <button className="profile-trigger" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar">
          <span className="profile-avatar">{INITIALS(authUser?.username)}</span>
          <span>
            <span className="profile-name">{authUser?.username}</span><br/>
            <span className="profile-role">{ROLE_LABELS[authUser?.role] || authUser?.role}</span>
          </span>
          <Menu size={14} color="var(--muted)" style={{marginLeft: 4}} />
        </button>
        <div className="brand" style={{marginLeft: 120}}>
          <div className="logo">IE</div>
          <div>
            <h1>IEU Sales-RM Activity Dashboard</h1>
            <p className="subtitle">
              {fmtN(filterOpts.total_rows)} aktivitas · {ROLE_LABELS[authUser?.role] || authUser?.role} view
              {filterOpts.data_last_updated && (
                <> · <span data-testid="data-freshness" style={{color: "var(--good)"}}>
                  Data terbaru: {new Date(filterOpts.data_last_updated).toLocaleString("id-ID", {dateStyle: "medium", timeStyle: "short"})}
                </span></>
              )}
            </p>
          </div>
        </div>
        <div className="actions">
          <GlobalSearch onSelectLead={(id) => {
            setSelectedLeadId(id);
            setLeadFormMode(null);
            setActiveView("leads");
          }} />
          <button className="btn btn-ghost" onClick={fetchDashboard}><RefreshCw size={14} /> Refresh</button>
          {perms.upload && ["admin", "manager"].includes(authUser?.role) && (
            <button className="btn btn-ghost" onClick={() => setUploadOpen(true)} data-testid="btn-upload-open"><Upload size={14} /> Upload</button>
          )}
          {perms.entry && (
            <button className="btn btn-ghost"
              onClick={() => { setSelectedLeadId(null); setLeadFormMode("create"); setActiveView("leads"); }}
              data-testid="btn-entry-open">
              <FileSignature size={14} /> Generate New Lead
            </button>
          )}
          {perms.download && (
            <button className="btn btn-primary" onClick={onDownload} data-testid="btn-download-excel"><Download size={14} /> Download Excel</button>
          )}
          {perms.audit && (
            <button className="btn btn-ghost" onClick={sendAuditEmailNow} disabled={auditSending} data-testid="btn-audit-email"
              title="Kirim email audit log sekarang (cron otomatis 23:59 WIB)">
              {auditSending ? <RefreshCw size={14} className="spin" /> : <Mail size={14} />} Audit Email
            </button>
          )}
        </div>
      </header>

      {auditStatus && (
        <div className={`alert ${auditStatus.ok ? "ok" : "err"}`} style={{ margin: "8px 24px 0 24px" }} data-testid="audit-status">
          <Mail size={14}/> {auditStatus.msg}
        </div>
      )}

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)}
        onSuccess={() => { loadFilterOpts(); fetchDashboard(); setUploadOpen(false); setDetailsRefreshKey((k) => k + 1); }} />
      <DrillDownModal open={!!drill} onClose={() => setDrill(null)} drill={drill} filters={apiFilters} />

      <div className="filterbar daterow" style={{display: activeView === "dashboard" ? "" : "none"}}>
        <div className="field"><label>Tanggal Dari</label>
          <input type="date" className="select-input" value={filters.date_from}
            onChange={(e) => setFilters({...filters, date_from: e.target.value})} data-testid="filter-date-from" />
        </div>
        <div className="field"><label>Tanggal Sampai</label>
          <input type="date" className="select-input" value={filters.date_to}
            onChange={(e) => setFilters({...filters, date_to: e.target.value})} data-testid="filter-date-to" />
        </div>
        <div style={{fontSize:11,color:"var(--muted)",alignSelf:"end",padding:"10px 0"}}>
          Rentang tanggal kustom · TA = 1 Aug — 31 Jul (Aug 2024–Jul 2025)
        </div>
      </div>

      <div className="filterbar filterbar-7" style={{display: activeView === "dashboard" ? "" : "none"}}>
        <FilterSelect label="Tim Sales" value={filters.tim} testid="filter-tim"
          onChange={(v) => setFilters({ ...filters, tim: v })} options={filterOpts.tim_sales} />
        <FilterSelect label="Nama Sales" value={filters.sales} testid="filter-sales"
          onChange={(v) => setFilters({ ...filters, sales: v })} options={filterOpts.nama_sales} />
        <FilterSelect label="Tahun Ajaran" value={filters.ta} testid="filter-ta"
          onChange={(v) => setFilters({ ...filters, ta: v })} options={filterOpts.tahun_ajaran} />
        <FilterSelect label="Status Sekolah" value={filters.status} testid="filter-status"
          onChange={(v) => setFilters({ ...filters, status: v })} options={filterOpts.status_sekolah} />
        <FilterSelect label="Dilakukan Via" value={filters.via} testid="filter-via"
          onChange={(v) => setFilters({ ...filters, via: v })} options={filterOpts.dilakukan_via} />
        <FilterSelect label="Tujuan Sales" value={filters.tujuan} testid="filter-tujuan"
          onChange={(v) => setFilters({ ...filters, tujuan: v })} options={filterOpts.tujuan_sales} />
        <MultiSelect label="Kegiatan (multi)" values={filters.kegiatan} testid="filter-kegiatan"
          onChange={(v) => setFilters({ ...filters, kegiatan: v })} options={filterOpts.kegiatan} />
        <div className="reset-wrap">
          <button className="btn btn-secondary" onClick={onReset} data-testid="btn-reset-filters">
            <RefreshCw size={14} /> Reset
          </button>
        </div>
      </div>

      <main className="main">
        {activeView === "users" && perms.user_mgmt && <UserManagement />}
        {activeView === "leads" && !selectedLeadId && !leadFormMode && (
          <LeadsListView
            onCreateNew={() => setLeadFormMode("create")}
            onSelectLead={(id) => setSelectedLeadId(id)}
          />
        )}
        {activeView === "leads" && leadFormMode && (
          <LeadFormView
            mode={leadFormMode}
            leadId={leadFormMode === "edit" ? selectedLeadId : null}
            onSuccess={() => { setLeadFormMode(null); setSelectedLeadId(null); }}
            onCancel={() => { setLeadFormMode(null); }}
          />
        )}
        {activeView === "leads" && selectedLeadId && !leadFormMode && (
          <LeadDetailView
            leadId={selectedLeadId}
            onBack={() => setSelectedLeadId(null)}
            onEdit={(id) => { setSelectedLeadId(id); setLeadFormMode("edit"); }}
          />
        )}
        {activeView === "activities" && perms.entry && (
          <ActivityFormView
            mode={editTarget ? "edit" : "create"}
            initialData={editTarget}
            onSuccess={() => { setEditTarget(null); setActiveView("dashboard"); loadFilterOpts(); fetchDashboard(); setDetailsRefreshKey((k) => k + 1); }}
            onCancel={() => { setEditTarget(null); setActiveView("dashboard"); }}
          />
        )}
        {activeView === "forecast" && <ForecastView />}
        {activeView === "comparison" && <SalesComparisonView />}
        {activeView === "chat" && <ChatView currentUser={authUser?.username} />}
        {activeView === "eft" && <EFTView currentUser={authUser} />}
        {activeView === "active-accounts" && <ActiveAccountView currentUser={authUser} />}
        {activeView === "churn-accounts" && <ChurnAccountView currentUser={authUser} />}
        {activeView === "approval" && <ApprovalView />}
        {activeView === "profile" && <ProfileView currentUser={authUser} />}
        {activeView !== "users" && activeView !== "activities" && activeView !== "leads"
          && activeView !== "forecast" && activeView !== "comparison"
          && activeView !== "chat" && activeView !== "active-accounts"
          && activeView !== "churn-accounts" && activeView !== "approval"
          && activeView !== "eft"
          && activeView !== "profile" && (<>
        <div className="section-title">Ringkasan Performa</div>
        <div className="grid kpi-grid" style={{ marginBottom: 14 }}>
          <KpiCard icon={GraduationCap} variant="teachcast" label="% TeachCast Partnership"
            value={loading ? "…" : fmtPct(data?.kpi?.pct_teachcast)}
            sub={loading ? "" : `${fmtN(data?.kpi?.teachcast_n)} dari ${fmtN(data?.kpi?.total_aktivitas)}`}
            testid="kpi-teachcast"
            onClick={() => openDrill({ title: "TeachCast Partnership Activities", key: "kpi_teachcast" })} />
          <KpiCard icon={Award} variant="eft" label="% EFT Activity"
            value={loading ? "…" : fmtPct(data?.kpi?.pct_eft)}
            sub={loading ? "" : `${fmtN(data?.kpi?.eft_n)} dari ${fmtN(data?.kpi?.total_aktivitas)}`}
            testid="kpi-eft"
            onClick={() => openDrill({ title: "EFT Purposed Activities", key: "kpi_eft" })} />
        </div>
        {(authUser?.role === "admin" || authUser?.role === "manager") && (<>
          <AdminMetricsPanel />
          <AdminChartsPanel />
        </>)}
        {authUser?.role === "sales_tl" && (<>
          <SalesTLDashboardPanel />
          <SalesTLChartsPanel />
        </>)}
        {authUser?.role === "sales" && (<>
          <SalesDashboardPanel />
          <SalesChartsPanel />
        </>)}
        {authUser?.role === "rm" && (<>
          <RMDashboardPanel />
          <RMChartsPanel />
        </>)}
        <div style={{ marginTop: 18 }}>
          <ConversionFunnel />
        </div>
        {(authUser?.role === "admin" || authUser?.role === "manager" || authUser?.role === "sales_tl") && (
          <TopPerformersPanel />
        )}

        <div className="section-title" style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={14} color="var(--accent)" /> AUTO-ANALYTICS — TERSEGMENTASI PER TUJUAN SALES
        </div>
        <InsightsPanel filters={apiFilters} onDrill={openDrill} />

        <div className="section-title" style={{ marginTop: 22 }}>Distribusi & Peringkat Kegiatan</div>
        <div className="grid chart-row">
          <ChartCard title="Distribusi Status Sekolah" subtitle="Demografi prospek (persentase)">
            {loading || !data ? <div className="empty">Memuat...</div> : (<>
              <DonutChart data={data.status_distribution} onSliceClick={(name) =>
                openDrill({ title: `Status: ${name}`, key: "status_sekolah", value: name })} />
              <StatusLegend data={data.status_distribution} />
            </>)}
          </ChartCard>
          <ChartCard title="Peringkat Kegiatan" subtitle="Klik bar untuk drill-down ke aktivitas">
            {loading || !data ? <div className="empty">Memuat...</div> :
              <HorizontalBar data={data.kegiatan_ranking} onBarClick={(name) =>
                openDrill({ title: `Kegiatan: ${name}`, key: "kegiatan", value: name })} />}
          </ChartCard>
        </div>

        <div className="section-title" style={{ marginTop: 22 }}>Performa Sales & Tren</div>
        <div className="grid row-split">
          <ChartCard title="Sales Leaderboard · Top Performer" subtitle="Ranking: EFT Closed terbanyak lalu Siswa TeachCast · klik baris untuk drill-down"
            action={<Users size={16} color="var(--accent)" />}>
            {loading || !data ? <div className="empty">Memuat...</div> :
              <Leaderboard data={data.leaderboard} onRowClick={(r) =>
                openDrill({ title: `Sales: ${r.nama_sales}`, key: "nama_sales", value: r.nama_sales })} />}
          </ChartCard>
          <ChartCard title="Timeline Aktivitas" subtitle="Klik titik untuk drill ke bulan tsb"
            action={<TrendingUp size={16} color="var(--accent)" />}>
            {loading || !data ? <div className="empty">Memuat...</div> :
              data.timeline.length === 0 ? <div className="empty">Tidak ada data periode.</div> :
              <TimelineChart data={data.timeline} onPointClick={(bulan) =>
                openDrill({ title: `Bulan: ${bulan}`, key: "bulan_visit", value: bulan })} />}
          </ChartCard>
        </div>

        <div className="section-title" style={{ marginTop: 22 }}>Daftar Aktivitas (Sortable)</div>
        <DetailsTable filters={apiFilters} canEdit={!!perms.edit}
          onEdit={handleEditRow} refreshKey={detailsRefreshKey} />
        </>)}
      </main>

      <footer className="footer">
        <strong>IEU Sales-RM Activity Dashboard</strong> · Enterprise CRM · {fmtN(filterOpts.total_rows)} aktivitas ·
        TA = 1 Aug – 31 Jul ·
        <a href={`${API}/sales/download/excel`} style={{ color: "var(--primary)", fontWeight: 600, marginLeft: 4 }}>Download Excel</a>
      </footer>
    </div>
    </DrilldownProvider>
  );
}

